import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RouteIcon from "@mui/icons-material/Route";
import LoopIcon from "@mui/icons-material/Loop";
import MuiMenu, { IMuiMenuItemProps } from "../framework/muiMenu";
import { EmployeeEditor, VehicleEditor, RouteEditor } from "./WorkShiftFieldEditors";
import { TimePickerInlineEdit } from "./TimePickerInlineEdit";
import { PlannedWorkShiftDto } from "../../models/plannedWorkShift/plannedWorkShift";
import { tooltipProps } from "./utils/OverlapTooltip";
import { getRoute } from "../../services/routeService";
import { RouteDialog } from "./RouteDialog";
import { RouteSaveDto } from "../../models/route/route";
import { WorkShiftChips } from "./WorkShiftChips";

interface ShiftCardCompactProps {
    shift: PlannedWorkShiftDto;
    timeLabel: string;
    onTimeSave: (startTime: string, endTime: string) => void;
    menuItems: IMuiMenuItemProps[];
    isRecurring: boolean;
    recurrenceLabel: string | null;
    employeeHasConflict?: boolean;
    vehicleHasConflict?: boolean;
    onSaveEmployee: (value: string | null) => Promise<void>;
    onSaveVehicle: (value: string | null) => Promise<void>;
    onSaveRoute: (value: string | null) => Promise<void>;
    onOpenRecurrenceEditor?: (event?: React.MouseEvent) => void;
    onCreateRoute?: () => void;
    actualShiftsDuration: number;
    plannedDuration: number;
}

const IconBadge: React.FC<{ bg?: string; children: React.ReactNode }> = ({ bg = "grey.500", children }) => (
    <Box
        sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: 20,
            minWidth: 20,
            px: 0.5,
            borderRadius: 0.5,
            bgcolor: bg,
            lineHeight: 0,
        }}
    >
        {children}
    </Box>
);

export const ShiftCardCompact = ({
    shift,
    timeLabel,
    onTimeSave,
    menuItems,
    isRecurring,
    recurrenceLabel,
    employeeHasConflict = false,
    vehicleHasConflict = false,
    onSaveEmployee,
    onSaveVehicle,
    onSaveRoute,
    onOpenRecurrenceEditor,
    actualShiftsDuration,
    plannedDuration,
}: ShiftCardCompactProps) => {
    const { t } = useTranslation();
    const [editor, setEditor] = useState<"employee" | "vehicle" | "route" | null>(null);
    const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
    const iconsRef = useRef<HTMLDivElement | null>(null);

    const hasEmployee = !!shift.employeeId;
    const hasVehicle = !!shift.vehicleId;
    const hasRoute = !!shift.routeId;

    const vehicleBg = vehicleHasConflict ? "error.main" : hasVehicle ? "primary.main" : "grey.400";
    const employeeBg = employeeHasConflict ? "error.main" : hasEmployee ? "primary.main" : "grey.400";
    const [routeInactive, setRouteInactive] = useState<boolean>(false);
    const routeBg = hasRoute ? "primary.main" : "grey.400";

    const employeeName = shift.employeeName ?? t("common.notSelected");
    const vehicleName = shift.vehicleName ?? t("common.notSelected");
    const routeName = shift.routeName ?? t("common.notSelected");

    const [createRouteOpen, setCreateRouteOpen] = useState(false);
    const newRouteInitial: RouteSaveDto = useMemo(
        () => ({ id: null, rowId: null, name: "", comment: null, isActive: true }),
        []
    );

    useEffect(() => {
        let mounted = true;
        if (!shift.routeId) {
            setRouteInactive(false);
            return;
        }
        getRoute(shift.routeId)
            .then(r => mounted && setRouteInactive(r?.isActive === false))
            .catch(() => mounted && setRouteInactive(false));
        return () => { mounted = false; };
    }, [shift.routeId]);

    const openEditor = (type: "employee" | "vehicle" | "route") =>
        (e: React.MouseEvent<HTMLElement>) => {
            const target = e.currentTarget as HTMLElement;
            const rect = target.getBoundingClientRect();
            const parentRect = iconsRef.current?.getBoundingClientRect();
            if (parentRect) {
                const left = Math.max(0, rect.left - parentRect.left);
                const top = rect.bottom - parentRect.top + 4;
                setPanelPos({ top, left });
            } else {
                setPanelPos({ top: target.offsetHeight + 4, left: 0 });
            }
            setEditor(type);
        };
    const closeEditor = () => {
        setEditor(null);
        setPanelPos(null);
    };

    const editorNode = (
        <Box p={1} minWidth={80}>
            {editor === "employee" && (
                <EmployeeEditor
                    shift={shift}
                    startOpen
                    handleSave={async(val: string | null) => {
                        await onSaveEmployee(val ?? null);
                        closeEditor();
                    }}
                />
            )}
            {editor === "vehicle" && (
                <VehicleEditor
                    shift={shift}
                    startOpen
                    handleSave={async(val: string | null) => {
                        await onSaveVehicle(val ?? null);
                        closeEditor();
                    }}
                />
            )}
            {editor === "route" && (
                <RouteEditor
                    shift={shift}
                    startOpen
                    handleSave={async(val: string | null) => {
                        await onSaveRoute(val ?? null);
                        closeEditor();
                    }}
                    onCreateRoute={() => {
                        setCreateRouteOpen(true);
                        closeEditor();
                    }}
                />
            )}
        </Box>
    );

    return (
        <Stack spacing={0.5}>
            <Box display="flex" alignItems="center" gap={0.5}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TimePickerInlineEdit
                        label={timeLabel}
                        start={shift.startTime}
                        end={shift.endTime}
                        handleSave={(startTime, endTime) =>
                            void onTimeSave(startTime, endTime)
                        }
                        hideClockIcon
                    />
                </Box>
                <MuiMenu
                    id={`workshift-actions-${shift.id}`}
                    label={<MoreVertIcon fontSize="small" />}
                    size="small"
                    items={menuItems}
                    buttonSx={{ p: 0.5 }}
                    noBox
                />
            </Box>

            <Stack
                sx={{ pb: 0.5 }}
                direction="row"
                gap={0.5}
                alignItems="center"
            >
                <WorkShiftChips
                    plannedShiftId={shift.id}
                    actualShiftsDuration={actualShiftsDuration}
                    plannedDuration={plannedDuration}
                />
            </Stack>

            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={0.5}
            >
                <Box
                    display="inline-flex"
                    alignItems="center"
                    gap={0.5}
                    ref={iconsRef}
                    sx={{ position: "relative" }}
                >
                    <Tooltip
                        title={t("workShiftPlanning.icons.vehicleTooltip", {
                            name: vehicleName,
                        })}
                        {...tooltipProps}
                    >
                        <span
                            onClick={openEditor("vehicle")}
                            style={{ cursor: "pointer" }}
                        >
                            <IconBadge bg={vehicleBg}>
                                <LocalShippingOutlinedIcon
                                    sx={{ fontSize: 16, color: "common.white" }}
                                />
                            </IconBadge>
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={t("workShiftPlanning.icons.employeeTooltip", {
                            name: employeeName,
                        })}
                        {...tooltipProps}
                    >
                        <span
                            onClick={openEditor("employee")}
                            style={{ cursor: "pointer" }}
                        >
                            <IconBadge bg={employeeBg}>
                                <PersonIcon
                                    sx={{ fontSize: 16, color: "common.white" }}
                                />
                            </IconBadge>
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={t("workShiftPlanning.icons.routeTooltip", {
                            name: `${routeName}${routeInactive ? t("route.inactiveSuffix") : ""}`,
                        })}
                        {...tooltipProps}
                    >
                        <span
                            id={`workshift-compact-route-icon-${shift.id}`}
                            onClick={openEditor("route")}
                            style={{ cursor: "pointer" }}
                        >
                            <IconBadge bg={routeBg}>
                                <RouteIcon
                                    sx={{ fontSize: 16, color: "common.white" }}
                                />
                            </IconBadge>
                        </span>
                    </Tooltip>

                    {editor && panelPos ? (
                        <ClickAwayListener onClickAway={closeEditor}>
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: panelPos.top,
                                    left: panelPos.left,
                                    zIndex: (theme) => theme.zIndex.tooltip + 1,
                                }}
                                onClick={(e: React.MouseEvent) =>
                                    e.stopPropagation()
                                }
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: -6,
                                        left: 8,
                                        width: 10,
                                        height: 10,
                                        bgcolor: "background.paper",
                                        transform: "rotate(45deg)",
                                        borderLeft: "1px solid",
                                        borderTop: "1px solid",
                                        borderColor: "divider",
                                    }}
                                />
                                <Paper elevation={6}>{editorNode}</Paper>
                            </Box>
                        </ClickAwayListener>
                    ) : null}
                </Box>

                {isRecurring ? (
                    <Tooltip title={recurrenceLabel ?? ""}>
                        <Box
                            position="relative"
                            display="inline-block"
                            sx={{ lineHeight: 0, cursor: "pointer" }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenRecurrenceEditor?.(e);
                            }}
                            role="button"
                        >
                            <LoopIcon
                                sx={{ color: "text.disabled" }}
                                fontSize="small"
                            />
                            {shift.isOverridenFromRecurrence ? (
                                <Box
                                    position="absolute"
                                    top="50%"
                                    left={0}
                                    width="100%"
                                    height="2px"
                                    bgcolor="text.disabled"
                                    style={{ transform: "rotate(35deg)" }}
                                />
                            ) : null}
                        </Box>
                    </Tooltip>
                ) : null}
            </Box>

            <RouteDialog
                open={createRouteOpen}
                item={newRouteInitial}
                enableIsActive
                onClose={() => setCreateRouteOpen(false)}
                onSaved={(route) => {
                    void onSaveRoute(route.id);
                    setCreateRouteOpen(false);
                }}
            />
        </Stack>
    );
};